import { AppSidebar } from '@/components/app-sidebar'
import { BreadcrumbNav } from '@/components/breadcrumb-nav'
import { Separator } from '@klear/ui/components/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@klear/ui/components/sidebar'
import { Combobox } from '@klear/ui/components/combobox'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useTraders } from '@klear/ui/hooks/use-traders'
import NotFound from '@/app/not-found'
import {
  ROUTE_CUSTOMERS,
  ROUTE_EXTERNAL_BANK,
  ROUTE_PAYABLE,
  ROUTE_RECEIVABLE,
  ROUTE_SUPPLIERS,
  buildNavPathTrader
} from '@klear/constants'
import { useQueryClient } from '@tanstack/react-query'
import { ErrorBoundary } from 'react-error-boundary'
import { TraderErrorFallback } from '@/components/trader-error-fallback'
import { useClientErrorReporting } from '@/hooks/use-client-error-reporting'
import { cn } from '@klear/ui/lib/utils'
import { AgentConsole } from '@klear/ui/components/agent-console'
import { AgentContextProvider } from '@klear/ui/contexts/agent-context'

function isWizardRoute(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean)
  const appIndex = segments.findIndex(
    (s) =>
      s === ROUTE_PAYABLE ||
      s === ROUTE_RECEIVABLE ||
      s === ROUTE_CUSTOMERS ||
      s === ROUTE_SUPPLIERS ||
      s === ROUTE_EXTERNAL_BANK
  )
  if (appIndex < 0) return false
  const segment = segments[appIndex]
  if (segment === ROUTE_PAYABLE || segment === ROUTE_RECEIVABLE || segment === ROUTE_EXTERNAL_BANK) return true
  if (segment === ROUTE_CUSTOMERS || segment === ROUTE_SUPPLIERS) {
    return segments.length > appIndex + 1
  }
  return false
}

export default function TraderLayout() {
  const { user } = useAuth()
  const { traderId } = useParams()
  const location = useLocation()
  const onReportError = useClientErrorReporting()
  const isValidTraderId = traderId && !isNaN(parseInt(traderId)) && /^\d+$/.test(traderId)

  const { trader: currentTrader, isLoading: isTraderLoading, error } = useTrader(isValidTraderId ? traderId : undefined)
  const { traders, isLoading } = useTraders()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  if (!isValidTraderId || (traderId && !isTraderLoading && (error || !currentTrader))) {
    return <NotFound />
  }

  if (!currentTrader || !user) {
    return null
  }

  const changeTrader = (traderId: string) => {
    if (traderId) {
      queryClient.invalidateQueries()
      navigate(buildNavPathTrader(traderId))
    }
  }

  return (
    <AgentContextProvider>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center px-4 gap-2 bg-background transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            {traders && traders.length > 1 ? (
              <Combobox
                className="max-w-xs"
                options={traders.map((trader) => ({
                  value: trader.id.toString(),
                  label: trader.name
                }))}
                value={currentTrader.id.toString()}
                onChange={changeTrader}
                disabled={isLoading}
                placeholder={`Search traders...`}
              />
            ) : (
              <div className="text-foreground">{currentTrader.name}</div>
            )}
            <Separator orientation="vertical" className="m-2 h-4" />
            <BreadcrumbNav />
          </header>
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col',
              isWizardRoute(location.pathname) ? 'overflow-hidden' : 'gap-4 p-4 pt-0'
            )}
          >
            <ErrorBoundary
              FallbackComponent={(props) => <TraderErrorFallback {...props} variant="content" />}
              onError={onReportError}
              resetKeys={[location.pathname]}
            >
              <Outlet />
            </ErrorBoundary>
          </div>
          {user.hasKlearRole && <AgentConsole key={currentTrader.id} traderId={currentTrader.id} />}
        </SidebarInset>
      </SidebarProvider>
    </AgentContextProvider>
  )
}
